import React, { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/features/auth/api/authApi';
import { useStudentAuthStore } from '@/features/auth/store/authStore';
import { StudentLayout } from '../layouts/StudentLayout';
import { StudentThemeBoundary } from '../theme/StudentThemeBoundary';

const LoginPage = React.lazy(() => import('@/features/auth/pages/LoginPage'));
const OnboardingPage = React.lazy(() => import('@/features/onboarding/pages/OnboardingPage'));
const LobbyPage = React.lazy(() => import('@/features/lobby/pages/LobbyPage'));
const PracticeEntryPage = React.lazy(() => import('@/features/practice/pages/PracticeEntryPage'));
const PracticePage = React.lazy(() => import('@/features/practice/pages/PracticePage'));
const AttemptResultPage = React.lazy(() => import('@/features/practice/pages/AttemptResultPage'));
const ProfilePage = React.lazy(() => import('@/features/profile/pages/ProfilePage'));
const LearningHomePage = React.lazy(() => import('@/features/learning/pages/LearningHomePage'));
const LearningCoursePage = React.lazy(() => import('@/features/learning/pages/LearningCoursePage'));
const LearningLessonPage = React.lazy(() => import('@/features/learning/pages/LearningLessonPage'));
const LearningExercisePage = React.lazy(() => import('@/features/learning/pages/LearningExercisePage'));
const LearningReviewPage = React.lazy(() => import('@/features/learning/pages/LearningReviewPage'));
const LeaderboardPage = React.lazy(() => import('@/features/leaderboard/pages/LeaderboardPage'));

function LoadingFallback() {
  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-6">
      <div className="mx-auto grid max-w-[1200px] gap-6">
        <div className="rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-6 shadow-[var(--shadow-sm)]">
          <div className="h-3 w-28 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
          <div className="mt-4 h-8 w-64 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
          <div className="mt-3 h-4 w-full max-w-[520px] rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
        </div>
        <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <div className="rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--shadow-sm)]">
            <div className="h-3 w-24 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
            <div className="mt-4 space-y-3">
              {[0, 1, 2, 3].map((index) => (
                <div key={`route-loading-sidebar-${index}`} className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-3">
                  <div className="h-3 w-20 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
                  <div className="mt-3 h-4 w-full rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
                  <div className="mt-2 h-4 w-3/4 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--shadow-sm)]">
            <div className="h-3 w-20 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[0, 1, 2, 3].map((index) => (
                <div key={`route-loading-card-${index}`} className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-4">
                  <div className="h-4 w-24 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
                  <div className="mt-3 h-4 w-full rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
                  <div className="mt-2 h-4 w-2/3 rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequireStudentAuth() {
  const accessToken = useStudentAuthStore((state) => state.accessToken);
  return accessToken ? <Outlet /> : <Navigate to="/login" replace />;
}

function RequireCompletedRegistration() {
  const user = useStudentAuthStore((state) => state.user);
  return user?.registrationCompleted ? <Outlet /> : <Navigate to="/onboarding" replace />;
}

function RequireStudentCapability({ capability }: { capability: 'learning' | 'fullMock' | 'leaderboard' }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['student-shell-settings'],
    queryFn: ({ signal }) => authApi.getShellSettings({ signal }),
    staleTime: 60_000,
  });
  if (isLoading) return <LoadingFallback />;
  if (isError || !data?.capabilities?.[capability]) return <Navigate to="/lobby" replace />;
  return <Outlet />;
}

export function AppRouter() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequireStudentAuth />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route element={<RequireCompletedRegistration />}>
            <Route
              path="/practice-entry/:questionId"
              element={
                <StudentThemeBoundary>
                  <PracticeEntryPage />
                </StudentThemeBoundary>
              }
            />
            <Route element={<StudentLayout />}>
              <Route path="/lobby" element={<LobbyPage />} />
              <Route element={<RequireStudentCapability capability="learning" />}>
                <Route path="/learning" element={<LearningHomePage />} />
                <Route path="/learning/courses/:courseId" element={<LearningCoursePage />} />
                <Route path="/learning/lessons/:lessonId" element={<LearningLessonPage />} />
                <Route path="/learning/attempts/:attemptId" element={<LearningExercisePage />} />
                <Route path="/learning/review" element={<LearningReviewPage />} />
              </Route>
              <Route element={<RequireStudentCapability capability="leaderboard" />}>
                <Route path="/rankings" element={<LeaderboardPage />} />
              </Route>
              <Route path="/practice/:questionId?" element={<PracticePage />} />
              <Route path="/attempts/:attemptId" element={<AttemptResultPage />} />
              <Route path="/profile" element={<ProfilePage />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}
